import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Home, Plus } from "lucide-react";

import { getHouseholds } from "@/api/households";
import { selectHouseholdsNeedingAttention } from "@/features/households/attention";
import { PageHero } from "@/components/layout/PageHero";
import { Button } from "@/components/ui/button";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import {
  DataTable,
  DataTableToolbar,
  PageContainer,
  SearchBar,
  type DataTableColumn,
} from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { HouseholdsNeedingAttention } from "@/features/households/components/HouseholdsNeedingAttention";
import type { Household } from "@/types/household";

export function HouseholdsPage() {
  const { churchId } = useAuth();
  const location = useLocation();
  const { permissions } = useEffectivePermissions();
  const [search, setSearch] = useState("");

  const householdsQuery = useQuery({
    queryKey: ["households", churchId],
    enabled: !!churchId,
    queryFn: () => getHouseholds(churchId!),
  });

  const households = householdsQuery.data ?? [];
  const attentionHouseholds = selectHouseholdsNeedingAttention(households);

  const filteredHouseholds = households.filter((household) => {
    const query = search.trim().toLowerCase();

    return (
      household.household_name.toLowerCase().includes(query) ||
      household.city?.toLowerCase().includes(query) ||
      household.state?.toLowerCase().includes(query) ||
      household.email?.toLowerCase().includes(query)
    );
  });

  const columns: DataTableColumn<Household>[] = [
    {
      key: "name",
      header: "Household",
      render: (household) => (
        <div>
          <Link
            to={`/households/${household.id}`}
            className="font-medium text-white hover:text-emerald-400"
          >
            {household.household_name}
          </Link>

          <p className="mt-1 text-xs text-slate-500">
            {household.address || "No address on file"}
          </p>
        </div>
      ),
    },
    {
      key: "members",
      header: "Members",
      render: (household) => (
        <span className="rounded-full bg-indigo-500/15 px-3 py-1 text-sm font-medium text-indigo-300">
          {household.member_count ?? "Unavailable"}
        </span>
      ),
    },
    {
      key: "health",
      header: "Health",
      render: (household) => {
        if (household.health_score === null) return <span className="text-slate-400">Not yet assessed</span>;
        const color =
          household.health_score >= 80
            ? "text-emerald-400"
            : household.health_score >= 60
              ? "text-amber-400"
              : "text-rose-400";

        return (
          <span className={`font-bold ${color}`}>
            {household.health_score}
          </span>
        );
      },
    },
    {
      key: "attendance",
      header: "Attendance",
      render: (household) => (
        <span>{household.attendance_rate === null ? "Not yet assessed" : `${household.attendance_rate}%`}</span>
      ),
    },
    {
      key: "care",
      header: "Care",
      render: (household) =>
        household.open_care_items === null ? <span className="text-slate-400">Not yet assessed</span> : household.open_care_items > 0 ? (
          <span className="rounded-full bg-rose-500/15 px-3 py-1 text-sm text-rose-400">
            {household.open_care_items}
          </span>
        ) : (
          <span className="text-slate-500">0</span>
        ),
    },
    {
      key: "lastAttendance",
      header: "Last Attendance",
      render: (household) =>
        household.last_attended_at
          ? new Date(household.last_attended_at).toLocaleDateString()
          : "-",
    },
    {
      key: "profile",
      header: "",
      render: (household) => (
        <Link
          to={`/households/${household.id}`}
          className="font-medium text-indigo-400 hover:text-indigo-300"
        >
          View
        </Link>
      ),
    },
  ];

  if (householdsQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (householdsQuery.isError || !householdsQuery.data) {
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load households"
          description="MinistryIQ could not load the household directory. Check your connection and try again."
          onRetry={() => void householdsQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const isRefreshing = householdsQuery.isFetching;

  return (
    <PageContainer>
      {location.state?.householdDeleted && <p role="status" className="mb-4 text-sm text-emerald-300">Household deleted successfully.</p>}
      <PageHero
        icon={<Home size={30} />}
        title="Households"
        subtitle="Manage families and household engagement."
        action={permissions.has("HOUSEHOLDS_MANAGE") &&
          <Button>
            <div className="flex items-center gap-2">
              <Plus size={18} />
              Add Household
            </div>
          </Button>
        }
      />

      <div className="mb-6">
        {permissions.has("INTELLIGENCE_VIEW") && <HouseholdsNeedingAttention households={attentionHouseholds} unassessed={households.filter(h => h.health_score === null).length} />}
      </div>

      <DataTableToolbar
        search={
          <SearchBar
            value={search}
            placeholder="Search households..."
            onChange={setSearch}
          />
        }
        meta={
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Home size={16} />
            <span>
              {isRefreshing
                ? "Refreshing households..."
                : `${filteredHouseholds.length} household(s)`}
            </span>
          </div>
        }
      />

      <DataTable
        data={filteredHouseholds}
        columns={columns}
        emptyTitle="No households found"
        emptyDescription="Try adjusting your search or add a household."
      />
    </PageContainer>
  );
}


